import './CartItem.scss';

import cn from 'classnames';
import React from 'react';
import Dotdotdot from 'react-dotdotdot';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import Flags from '@/common/components/atoms/Flags/Flags';
import Picture from '@/common/components/atoms/Picture/Picture';
import Price from '@/common/components/blocks/Price/Price';
import ButtonClose from '@/common/components/molecules/ButtonClose/ButtonClose';
import Quantity from '@/common/components/molecules/Quantity/Quantity';
import { ROLES } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import withPriceAndStock from '@/common/hoc/products/withPriceAndStock';
import useImage from '@/common/hooks/image/useImage';
import ProductPrice from '@/ebat/components/blocks/Common/Product/ProductPrice/ProductPrice';
import ProductAdditional from '@/ebat/components/blocks/ProductAdditional/ProductAdditional';
import { PRODUCTS_TYPES_FORMATS } from '@/ebat/constants';

import { CartItemDefaultProps, CartItemPropTypes } from './CartItem.proptypes';
import useCartItem from './useCartItem';

const CartItem = ({
  agrementFgaz,
  brand,
  className,
  disabled,
  discountClass,
  discountClasses,
  discountLabel,
  discountPriceItem,
  discountPriceTTC,
  flags,
  id,
  image,
  invoiceUnit,
  isLoading,
  isSalable,
  isSelected,
  isSubstitute,
  lineSubtotal,
  manufacturerSku,
  mustSubstitute,
  name,
  onModify,
  onRemove,
  path,
  price,
  publicPrice,
  publicPriceTTC,
  publicPriceTTCAmount,
  quantity,
  quoteNumber,
  saleUnit,
  salesPackaging,
  sku,
  slug,
  splitPackaging,
  substitute,
  weeeAmount,
  weeeTotalAmount,
}) => {
  const { t } = useTranslation();
  const { alt, src } = useImage({ image, format: PRODUCTS_TYPES_FORMATS.LIST });
  const { isShowNetPrice, user } = useAuth();

  const {
    isDisabled,
    onClickItem,
    valueDiscountPriceItem,
    valueLineSubtotal,
    valuePrice,
    valueWeeeAmount,
    valueWeeeTotalAmount,
  } = useCartItem({
    disabled,
    discountPriceItem,
    isLoading,
    isSalable,
    lineSubtotal,
    mustSubstitute,
    path,
    price,
    slug,
    weeeAmount,
    weeeTotalAmount,
  });

  return (
    <div
      className={cn('scanbat-cart-item', className, {
        'scanbat-cart-item--disabled': isDisabled.manage,
      })}
      data-qa-sku={sku}
    >
      <div
        className="scanbat-cart-item__item"
        id={`scanbat-cart-item-${id}`}
        role="button"
        tabIndex="0"
      >
        <Flags className="scanbat-cart-item__flags" flags={flags} />
        {path ? (
          <Button
            className="scanbat-cart-item__image"
            onClick={onClickItem}
            onKeyPress={onClickItem}
          >
            <Picture alt={alt} src={src} className="scanbat-cart-item__image" />
          </Button>
        ) : (
          <Picture alt={alt} src={src} className="scanbat-cart-item__image" />
        )}

        <h3 className="scanbat-cart-item__brand" title={brand.name}>
          {brand.name}
        </h3>
        <h4 className="scanbat-cart-item__model" title={name}>
          <Dotdotdot clamp={2}>{name}</Dotdotdot>
        </h4>
        <div className="scanbat-cart-item__sku">
          <span className="name">{t('common:entities:product.sku')}</span>
          {sku}
        </div>
        <div className="scanbat-cart-item__reference">
          <span className="name">
            {t('common:entities:product.manufacturer_sku')}
          </span>
          {manufacturerSku}
        </div>
        <div className="scanbat-cart-item__hr" />
        <div className="scanbat-cart-item__price price">
          {user.role !== ROLES.CUSTOMER_GUEST && (
            <div>
              <p className="price__label">
                {isShowNetPrice
                  ? t('common:messages:sort.customer_price')
                  : t('common:messages:sort.public_price')}
              </p>
              {discountPriceItem > 0 && (
                <Price
                  className="price__value--discount"
                  context="discount-price"
                  value={valueDiscountPriceItem}
                />
              )}
              <Price
                className={cn({
                  'price__value--old-price': discountPriceItem,
                })}
                emptyLabel={t('common:entities:product.price.not_price')}
                value={
                  user.role === ROLES.CUSTOMER_GUEST ? publicPrice : valuePrice
                }
              />
              <span className="price__unit">{invoiceUnit}</span>
              {weeeAmount > 0 && (
                <span className="price__weee">
                  {t('common:entities:product.price.weee_amount_long', {
                    amount: (
                      <Price
                        className="amount"
                        key={`weee-amount-${valueWeeeAmount}`}
                        sup={false}
                        value={valueWeeeAmount}
                      />
                    ),
                  })}
                </span>
              )}
            </div>
          )}
          {user.role === ROLES.CUSTOMER_GUEST && (
            <div>
              <p className="price__label">
                {t('common:entities:product.price.public_price_ttc')}
              </p>
              {discountPriceItem > 0 && (
                <Price
                  className="price__value--discount"
                  context="discount-price"
                  value={discountPriceTTC}
                />
              )}
              <Price
                className={cn({
                  'price__value--old-price': discountPriceItem,
                })}
                emptyLabel={t('common:entities:product.price.not_price')}
                value={publicPriceTTC}
              />
              <span className="price__unit">{invoiceUnit}</span>
              {weeeAmount > 0 && (
                <span className="price__weee">
                  {t('common:entities:product.price.weee_amount_long', {
                    amount: (
                      <Price
                        className="amount"
                        key={`weee-amount-${valueWeeeAmount}`}
                        sup={false}
                        value={valueWeeeAmount}
                      />
                    ),
                  })}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="scanbat-cart-item__total total">
          {user.role !== ROLES.CUSTOMER_GUEST ? (
            <div>
              <p className="total__label">
                {t('common:messages:sort.amount_total_raw')}
              </p>
              <span className="total__value">
                <Price value={valueLineSubtotal} />
              </span>
              {weeeTotalAmount > 0 && (
                <span className="price__weee">
                  {t('common:entities:product.price.weee_amount_long', {
                    amount: (
                      <Price
                        className="amount"
                        key={`weee-amount-${valueWeeeTotalAmount}`}
                        sup={false}
                        value={valueWeeeTotalAmount}
                      />
                    ),
                  })}
                </span>
              )}
            </div>
          ) : (
            <ProductPrice
              className="description__net-price"
              label={t('ebat:entities:common.aside.amount_ttc')}
              price={publicPriceTTCAmount}
            />
          )}
        </div>
        <Quantity
          className="scanbat-cart-item__quantity"
          disabled={isDisabled.manage}
          id={id}
          isLoading={isDisabled.loading}
          onChange={(data) => onModify({ ...data, isSelected })}
          onClick={(e) => e.stopPropagation()}
          quantity={quantity}
          salesPackaging={salesPackaging}
          splitPackaging={splitPackaging}
          unit={saleUnit}
        />
      </div>
      <ProductAdditional
        agrementFgaz={agrementFgaz}
        className="scanbat-cart-item__additional"
        disabled={isDisabled.delete}
        discountClass={discountClass}
        discountClasses={discountClasses}
        discountLabel={discountLabel}
        id={id}
        isLoading={isDisabled.loading}
        isNotSalable={isDisabled.notSalable}
        isSubstitute={isSubstitute}
        onRemove={onRemove}
        quoteNumber={quoteNumber}
        substitute={substitute}
      />
      <ButtonClose
        className="scanbat-cart-item__remove"
        disabled={isDisabled.manage}
        onClick={() => onRemove({ id })}
      />
    </div>
  );
};

CartItem.defaultProps = {
  ...CartItemDefaultProps,
};

CartItem.propTypes = {
  ...CartItemPropTypes,
};

export default withPriceAndStock(CartItem);
